import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getSocket, initializeSocket, disconnectSocket, isConnected, emitEvent, onEvent, offEvent } from '../services/socket';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: any;
  isConnected: boolean;
  reconnect: () => void;
  emit: (event: string, data?: any) => void;
  on: (event: string, callback: (data: any) => void) => () => void;
  off: (event: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Return a default context instead of throwing to prevent errors during lazy loading
    console.warn('[useSocket] Socket context not available, returning default context');
    return {
      socket: null,
      isConnected: false,
      reconnect: () => {},
      emit: () => {},
      on: () => () => {}, // Return cleanup function
      off: () => {},
    };
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<any>(null);
  const [connected, setConnected] = useState(false);
  const { user } = useAuth();

  const handleConnect = useCallback(() => {
    console.log('[SocketContext] Connection established');
    setConnected(true);
    
    // User is automatically registered by backend on connection
    // But we can still emit register-user for explicit re-registration if needed
    if (user?.id) {
      const currentSocket = getSocket();
      if (currentSocket) {
        console.log('[SocketContext] User already authenticated and registered:', user.id);
        // Optional: Explicitly register (backend will verify it matches authenticated user)
        currentSocket.emit('register-user', { userId: user.id });
      }
    }
  }, [user?.id]);

  const handleDisconnect = useCallback((reason: string) => {
    console.log('[SocketContext] Connection lost:', reason);
    setConnected(false);
    
    // If disconnected due to authentication error, don't attempt to reconnect
    if (reason === 'io server disconnect' || reason === 'transport close') {
      console.log('[SocketContext] Disconnected by server, will not auto-reconnect');
    }
  }, []);

  const reconnect = useCallback(() => {
    console.log('[SocketContext] Manual reconnect triggered');
    disconnectSocket();
    const newSocket = initializeSocket();
    setSocket(newSocket);
  }, []);

  const emit = useCallback((event: string, data?: any) => {
    emitEvent(event, data);
  }, []);

  const on = useCallback((event: string, callback: (data: any) => void) => {
    return onEvent(event, callback);
  }, []);

  const off = useCallback((event: string) => {
    offEvent(event);
  }, []);

  // Only initialize socket when user is authenticated
  useEffect(() => {
    // Don't connect if user is not authenticated
    if (!user) {
      console.log('[SocketContext] User not authenticated, skipping socket connection');
      // Disconnect existing socket if user logs out
      if (socket) {
        disconnectSocket();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // If socket already exists and is connected, don't reinitialize
    if (socket?.connected) {
      console.log('[SocketContext] Socket already connected, skipping initialization');
      return;
    }

    console.log('[SocketContext] User authenticated, initializing socket connection...');
    
    // Initialize socket (will authenticate using httpOnly cookie)
    const socketInstance = initializeSocket();
    setSocket(socketInstance);
    setConnected(socketInstance?.connected || false);

    // Set up connection status listeners
    socketInstance.on('connect', handleConnect);
    socketInstance.on('disconnect', handleDisconnect);
    socketInstance.on('reconnect', handleConnect);

    // Listen for financial approval events
    const handleFinancialApproval = (data: any) => {
      console.log('🎉 [SocketContext] Financial approval event received:', data);
      // You can add toast notification or other UI updates here
    };
    
    socketInstance.on('financial-approval-created', handleFinancialApproval);

    // Cleanup on unmount or when user changes
    return () => {
      console.log('[SocketContext] Cleaning up socket connection...');
      socketInstance.off('connect', handleConnect);
      socketInstance.off('disconnect', handleDisconnect);
      socketInstance.off('reconnect', handleConnect);
      socketInstance.off('financial-approval-created', handleFinancialApproval);
      // Note: We don't disconnect here to allow auto-reconnect on page refresh
      // Socket will be disconnected when user logs out (handled above)
    };
  }, [user, handleConnect, handleDisconnect]);

  // Re-register user when user changes
  useEffect(() => {
    if (connected && user?.id && socket) {
      console.log('[SocketContext] User changed, re-registering:', user.id);
      socket.emit('register-user', { userId: user.id });
    }
  }, [user?.id, connected, socket]);

  // Monitor connection status
  useEffect(() => {
    const interval = setInterval(() => {
      const currentStatus = isConnected();
      if (currentStatus !== connected) {
        setConnected(currentStatus);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [connected]);

  const value = useMemo<SocketContextType>(
    () => ({
      socket,
      isConnected: connected,
      reconnect,
      emit,
      on,
      off,
    }),
    [socket, connected, reconnect, emit, on, off],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

